import { type Upload, UploadStatus } from '@air/redux-uploader';

import {
  type FinishedUploadBatch,
  type UploadedBatchAttachment,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';

export type UploadBatchOfferUpload = Pick<
  Upload,
  'batchId' | 'clipId' | 'completedAt' | 'createdAt' | 'imageBase64' | 'status'
> & {
  apiUploadData: Pick<Upload['apiUploadData'], 'importedName'>;
  s3Info: { uploadUrlInfo?: { assetId: string } };
};

const inProgressStatuses: ReadonlySet<UploadStatus> = new Set([
  UploadStatus.preparing,
  UploadStatus.queued,
  UploadStatus.uploading,
  UploadStatus.paused,
  UploadStatus.stopped,
]);

export const isUploadInProgress = (status: UploadStatus): boolean => inProgressStatuses.has(status);

export const rememberWatchedUploadBatches = (
  watched: Set<string>,
  uploads: readonly UploadBatchOfferUpload[],
): void => {
  for (const upload of uploads) {
    if (isUploadInProgress(upload.status)) {
      watched.add(upload.batchId);
    }
  }
};

// The upload record already carries both ids and a local preview, so attaching needs no fetch.
const toUploadedBatchAttachment = (upload: UploadBatchOfferUpload): UploadedBatchAttachment[] => {
  const assetId = upload.s3Info.uploadUrlInfo?.assetId;

  if (!upload.clipId || !assetId) {
    return [];
  }

  return [
    {
      assetId,
      id: upload.clipId,
      isThumbnailPending: false,
      name: upload.apiUploadData.importedName || 'Untitled',
      thumbnailUrl: upload.imageBase64 ?? null,
      versionId: upload.clipId,
    },
  ];
};

const getBatchMaxCreatedAt = (batchUploads: readonly UploadBatchOfferUpload[]): number =>
  Math.max(...batchUploads.map((upload) => upload.createdAt));

export const getLatestFinishedUploadBatch = (
  uploads: readonly UploadBatchOfferUpload[],
  watchedBatchIds: ReadonlySet<string>,
): FinishedUploadBatch | null => {
  const uploadsByBatchId = new Map<string, UploadBatchOfferUpload[]>();

  for (const upload of uploads) {
    const batchUploads = uploadsByBatchId.get(upload.batchId);

    if (batchUploads) {
      batchUploads.push(upload);
    } else {
      uploadsByBatchId.set(upload.batchId, [upload]);
    }
  }

  let latestBatchId: string | null = null;
  let latestBatchMaxCreatedAt = -Infinity;

  for (const [batchId, batchUploads] of uploadsByBatchId) {
    const batchMaxCreatedAt = getBatchMaxCreatedAt(batchUploads);

    if (batchMaxCreatedAt > latestBatchMaxCreatedAt) {
      latestBatchMaxCreatedAt = batchMaxCreatedAt;
      latestBatchId = batchId;
    }
  }

  if (latestBatchId === null) {
    return null;
  }

  const latestBatchUploads = uploadsByBatchId.get(latestBatchId) ?? [];

  if (!watchedBatchIds.has(latestBatchId)) {
    return null;
  }

  if (latestBatchUploads.some((upload) => isUploadInProgress(upload.status))) {
    return null;
  }

  const completedUploads = latestBatchUploads.filter((upload) => upload.status === UploadStatus.completed);
  const attachments = completedUploads.flatMap(toUploadedBatchAttachment);

  if (attachments.length === 0) {
    return null;
  }

  const completedAtValues = completedUploads
    .map((upload) => upload.completedAt)
    .filter((completedAt): completedAt is number => completedAt !== undefined);

  const finishedAt =
    completedAtValues.length > 0
      ? Math.max(...completedAtValues)
      : Math.max(...completedUploads.map((upload) => upload.createdAt));

  return {
    batchId: latestBatchId,
    attachments,
    finishedAt,
    source: 'upload',
  };
};
